// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\CampaignGenerationStep.jsx
// Название: Campaign Generation Step - Full Featured
// Версия: 3.0.0
// Дата: 23.12.2025

import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';

const CampaignGenerationStep = ({ campaignId, onNext, onBack }) => {
  // ============================================
  // STATE
  // ============================================
  
  const [status, setStatus] = useState('idle'); // idle, generating, paused, completed, error
  const [progress, setProgress] = useState({
    total: 0,
    completed: 0,
    generating: 0,
    failed: 0,
    pending: 0,
    percentage: 0
  });
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const intervalRef = useRef(null);
  const timerRef = useRef(null);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    // Проверяем начальный статус при загрузке
    checkInitialStatus();
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [campaignId]);

  useEffect(() => {
    if (status === 'generating') {
      intervalRef.current = setInterval(checkStatus, 3000);
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  // ============================================
  // API CALLS
  // ============================================

  const checkInitialStatus = async () => {
    try {
      const response = await api.get('/campaigns/' + campaignId + '/generation-status');
      const data = response.data || response;
      
      updateProgressFromData(data);
      
      // Определяем статус
      if (data.status === 'generating' || (data.progress?.generating > 0)) {
        setStatus('generating');
        setStartTime(Date.now());
      } else if (data.status === 'completed' || data.status === 'ready') {
        setStatus('completed');
      } else if (data.progress?.failed > 0 && data.progress?.generating === 0) {
        setStatus('error');
      } else {
        // Автозапуск генерации если статус idle и есть получатели
        const total = data.progress?.total || data.total || 0;
        if (total > 0) {
          startGeneration();
        }
      }
    } catch (err) {
      console.error('Check initial status error:', err);
      addLog('error', 'Ошибка проверки статуса: ' + err.message);
    }
  };

  const checkStatus = async () => {
    try {
      const response = await api.get('/campaigns/' + campaignId + '/generation-status');
      const data = response.data || response;

      updateProgressFromData(data);

      // Проверяем завершение
      const prog = data.progress || data;
      if (data.status === 'completed' || data.status === 'ready' || 
          (prog.completed > 0 && prog.completed === prog.total && prog.generating === 0)) {
        setStatus('completed');
        addLog('success', 'Генерация завершена!');
        
        // Автопереход на результаты через 1.5 сек
        setTimeout(() => {
          onNext();
        }, 1500);
      } else if (prog.failed > 0 && prog.generating === 0 && prog.pending === 0) {
        setStatus('error');
        setError('Не удалось сгенерировать ' + prog.failed + ' изображений');
        addLog('error', 'Ошибка генерации: ' + prog.failed + ' failed');
      }
    } catch (err) {
      console.error('Check status error:', err);
      addLog('error', 'Ошибка проверки: ' + err.message);
    }
  };

  const updateProgressFromData = (data) => {
    const prog = data.progress || data || {};
    const total = parseInt(prog.total) || 0;
    const completed = parseInt(prog.completed) || 0;
    const generating = parseInt(prog.generating) || 0;
    const failed = parseInt(prog.failed) || 0;
    const pending = parseInt(prog.pending) || total - completed - generating - failed;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    setProgress({ total, completed, generating, failed, pending, percentage });
    
    // Оценка времени (2 сек на изображение)
    const remaining = total - completed;
    setEstimatedTime(remaining * 2);
  };

  const startGeneration = async () => {
    setStatus('generating');
    setError('');
    setStartTime(Date.now());
    setElapsedTime(0);
    addLog('info', 'Запуск генерации...');

    try {
      const response = await api.post('/campaigns/' + campaignId + '/generate-ai');
      const data = response.data || response;
      addLog('success', 'Генерация запущена');
      
      if (data.progress) {
        updateProgressFromData(data);
      }
      
      setTimeout(checkStatus, 1000);
    } catch (err) {
      setStatus('error');
      const errMsg = err.response?.data?.error || err.message || 'Ошибка запуска генерации';
      setError(errMsg);
      addLog('error', errMsg);
    }
  };

  const retryFailed = async () => {
    setStatus('generating');
    setError('');
    addLog('info', 'Повторная генерация для failed...');

    try {
      await api.post('/campaigns/' + campaignId + '/generate-ai', { retryFailed: true });
      addLog('success', 'Повторная генерация запущена');
      setTimeout(checkStatus, 1000);
    } catch (err) {
      setStatus('error');
      const errMsg = err.response?.data?.error || err.message || 'Ошибка';
      setError(errMsg);
      addLog('error', errMsg);
    }
  };

  const cancelGeneration = async () => {
    try {
      await api.post('/campaigns/' + campaignId + '/cancel-generation');
      setStatus('idle');
      addLog('info', 'Генерация отменена');
    } catch (err) {
      addLog('error', 'Ошибка отмены: ' + err.message);
    }
  };

  // ============================================
  // HELPERS
  // ============================================

  const addLog = (type, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { type, message, timestamp }]);
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: { maxWidth: 800, margin: '0 auto' },
    header: { textAlign: 'center', marginBottom: 30 },
    title: { fontSize: 24, fontWeight: 600, marginBottom: 8 },
    subtitle: { color: '#666', fontSize: 15 },
    
    // Card
    card: { background: 'white', borderRadius: 16, padding: 40, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
    cardCenter: { textAlign: 'center' },
    
    // Icon
    icon: { fontSize: 64, marginBottom: 20 },
    iconSpin: { animation: 'spin 2s linear infinite' },
    
    // Progress
    progressSection: { marginBottom: 30 },
    progressBar: { background: '#f3f4f6', borderRadius: 12, height: 24, overflow: 'hidden', marginBottom: 16 },
    progressFill: { height: '100%', borderRadius: 12, transition: 'width 0.5s ease', background: 'linear-gradient(90deg, #10B981, #059669)' },
    progressFillError: { background: 'linear-gradient(90deg, #f59e0b, #d97706)' },
    progressText: { fontSize: 32, fontWeight: 700, color: '#10B981', marginBottom: 8 },
    progressPercent: { color: '#666', fontSize: 14 },
    
    // Stats
    stats: { display: 'flex', justifyContent: 'center', gap: 24, marginTop: 24 },
    stat: { textAlign: 'center', padding: '12px 20px', background: '#f9fafb', borderRadius: 10 },
    statValue: { fontSize: 20, fontWeight: 600, marginBottom: 4 },
    statLabel: { fontSize: 12, color: '#6b7280' },
    
    // Time
    timeSection: { display: 'flex', justifyContent: 'center', gap: 30, marginTop: 20, color: '#666', fontSize: 14 },
    
    // Buttons
    btnPrimary: { 
      background: 'linear-gradient(135deg, #10B981, #059669)', 
      color: 'white', border: 'none', padding: '14px 32px', 
      borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 8
    },
    btnSecondary: { 
      background: '#f3f4f6', color: '#374151', border: 'none', 
      padding: '14px 24px', borderRadius: 10, fontSize: 15, cursor: 'pointer' 
    },
    btnDanger: { 
      background: '#fee2e2', color: '#dc2626', border: 'none', 
      padding: '12px 20px', borderRadius: 8, fontSize: 14, cursor: 'pointer' 
    },
    btnGroup: { display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 },
    
    // Alerts
    alert: { padding: '12px 16px', borderRadius: 10, marginBottom: 20 },
    alertError: { background: '#fee2e2', color: '#dc2626' },
    alertSuccess: { background: '#dcfce7', color: '#16a34a' },
    alertInfo: { background: '#dbeafe', color: '#1d4ed8' },
    
    // Logs
    logsToggle: { textAlign: 'center', marginTop: 20 },
    logsBtn: { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13 },
    logsContainer: { marginTop: 16, background: '#1f2937', borderRadius: 10, padding: 16, maxHeight: 200, overflowY: 'auto' },
    logEntry: { fontFamily: 'monospace', fontSize: 12, marginBottom: 4, display: 'flex', gap: 8 },
    logTime: { color: '#6b7280' },
    logInfo: { color: '#60a5fa' },
    logSuccess: { color: '#34d399' },
    logError: { color: '#f87171' },
    
    // Footer
    footer: { display: 'flex', gap: 12, justifyContent: 'center', marginTop: 30, paddingTop: 24, borderTop: '1px solid #e5e7eb' },
    
    // Description
    desc: { color: '#666', marginBottom: 30, fontSize: 15, lineHeight: 1.7 },
    features: { textAlign: 'left', background: '#f9fafb', padding: 20, borderRadius: 12, marginBottom: 30 },
    featureItem: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 14 },
    featureIcon: { width: 24, height: 24, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>🎨 AI Генерация подарков</h2>
        <p style={styles.subtitle}>Создание персонализированных изображений с помощью FLUX.1 AI</p>
      </div>

      <div style={styles.card}>
        {/* IDLE STATE */}
        {status === 'idle' && (
          <div style={styles.cardCenter}>
            <div style={styles.icon}>🚀</div>
            <p style={styles.desc}>
              Система создаст персональные AI-изображения для каждого получателя.<br/>
              Каждое изображение уникально и включает фото получателя.
            </p>
            
            <div style={styles.features}>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}>✓</span>
                <span>Персонализация с фото получателя</span>
              </div>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}>✓</span>
                <span>Встроенный QR-код для получения подарка</span>
              </div>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}>✓</span>
                <span>Высокое качество (1024×1024)</span>
              </div>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}>✓</span>
                <span>~2-3 секунды на изображение</span>
              </div>
            </div>

            {progress.total > 0 && (
              <div style={{...styles.alert, ...styles.alertInfo}}>
                📊 Готово к генерации: {progress.total} получателей
                {estimatedTime && <span> • ~{formatTime(estimatedTime)}</span>}
              </div>
            )}
            
            <button style={styles.btnPrimary} onClick={startGeneration}>
              🎨 Запустить генерацию
            </button>
          </div>
        )}

        {/* GENERATING STATE */}
        {status === 'generating' && (
          <div style={styles.cardCenter}>
            <div style={styles.icon}>⏳</div>
            <h3 style={{ marginBottom: 24 }}>Генерируем AI изображения...</h3>

            <div style={styles.progressSection}>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: progress.percentage + '%'}} />
              </div>
              
              <p style={styles.progressText}>{progress.completed} / {progress.total}</p>
              <p style={styles.progressPercent}>{progress.percentage}% завершено</p>
            </div>

            <div style={styles.stats}>
              <div style={styles.stat}>
                <div style={{...styles.statValue, color: '#10B981'}}>{progress.completed}</div>
                <div style={styles.statLabel}>Готово</div>
              </div>
              <div style={styles.stat}>
                <div style={{...styles.statValue, color: '#f59e0b'}}>{progress.generating}</div>
                <div style={styles.statLabel}>В процессе</div>
              </div>
              <div style={styles.stat}>
                <div style={{...styles.statValue, color: '#6b7280'}}>{progress.pending}</div>
                <div style={styles.statLabel}>Ожидают</div>
              </div>
              {progress.failed > 0 && (
                <div style={styles.stat}>
                  <div style={{...styles.statValue, color: '#dc2626'}}>{progress.failed}</div>
                  <div style={styles.statLabel}>Ошибки</div>
                </div>
              )}
            </div>

            <div style={styles.timeSection}>
              <span>⏱ Прошло: {formatTime(elapsedTime)}</span>
              {estimatedTime > 0 && <span>⏳ Осталось: ~{formatTime(estimatedTime)}</span>}
            </div>

            <div style={styles.btnGroup}>
              <button style={styles.btnDanger} onClick={cancelGeneration}>
                ✕ Отменить
              </button>
            </div>
          </div>
        )}

        {/* COMPLETED STATE */}
        {status === 'completed' && (
          <div style={styles.cardCenter}>
            <div style={styles.icon}>✅</div>
            <h3 style={{ color: '#10B981', marginBottom: 16 }}>Генерация завершена!</h3>
            <p style={{ color: '#666', marginBottom: 8 }}>
              Создано {progress.completed} персональных AI-подарков
            </p>
            {progress.failed > 0 && (
              <p style={{ color: '#f59e0b', marginBottom: 16 }}>
                ⚠️ Не удалось: {progress.failed}
              </p>
            )}
            
            <div style={styles.stats}>
              <div style={styles.stat}>
                <div style={{...styles.statValue, color: '#10B981'}}>{progress.completed}</div>
                <div style={styles.statLabel}>Успешно</div>
              </div>
              {progress.failed > 0 && (
                <div style={styles.stat}>
                  <div style={{...styles.statValue, color: '#dc2626'}}>{progress.failed}</div>
                  <div style={styles.statLabel}>Ошибки</div>
                </div>
              )}
              <div style={styles.stat}>
                <div style={styles.statValue}>{formatTime(elapsedTime)}</div>
                <div style={styles.statLabel}>Время</div>
              </div>
            </div>

            <div style={styles.btnGroup}>
              {progress.failed > 0 && (
                <button style={styles.btnSecondary} onClick={retryFailed}>
                  🔄 Повторить ошибки
                </button>
              )}
              <button style={styles.btnPrimary} onClick={onNext}>
                Посмотреть результаты →
              </button>
            </div>
          </div>
        )}

        {/* ERROR STATE */}
        {status === 'error' && (
          <div style={styles.cardCenter}>
            <div style={styles.icon}>⚠️</div>
            <h3 style={{ color: '#dc2626', marginBottom: 16 }}>Ошибка генерации</h3>
            
            <div style={{...styles.alert, ...styles.alertError}}>
              {error}
            </div>

            <div style={styles.stats}>
              <div style={styles.stat}>
                <div style={{...styles.statValue, color: '#10B981'}}>{progress.completed}</div>
                <div style={styles.statLabel}>Успешно</div>
              </div>
              <div style={styles.stat}>
                <div style={{...styles.statValue, color: '#dc2626'}}>{progress.failed}</div>
                <div style={styles.statLabel}>Ошибки</div>
              </div>
              <div style={styles.stat}>
                <div style={{...styles.statValue, color: '#6b7280'}}>{progress.pending}</div>
                <div style={styles.statLabel}>Не обработано</div>
              </div>
            </div>

            <div style={styles.btnGroup}>
              <button style={styles.btnSecondary} onClick={retryFailed}>
                🔄 Повторить ошибки
              </button>
              <button style={styles.btnSecondary} onClick={startGeneration}>
                🔁 Запустить заново
              </button>
              {progress.completed > 0 && (
                <button style={styles.btnPrimary} onClick={onNext}>
                  Продолжить с готовыми →
                </button>
              )}
            </div>
          </div>
        )}

        {/* LOGS */}
        <div style={styles.logsToggle}>
          <button style={styles.logsBtn} onClick={() => setShowLogs(!showLogs)}>
            {showLogs ? '▲ Скрыть логи' : '▼ Показать логи'} ({logs.length})
          </button>
        </div>
        
        {showLogs && logs.length > 0 && (
          <div style={styles.logsContainer}>
            {logs.map((log, i) => (
              <div key={i} style={styles.logEntry}>
                <span style={styles.logTime}>[{log.timestamp}]</span>
                <span style={
                  log.type === 'error' ? styles.logError :
                  log.type === 'success' ? styles.logSuccess :
                  styles.logInfo
                }>
                  {log.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div style={styles.footer}>
        <button style={styles.btnSecondary} onClick={onBack}>
          ← Назад к фото
        </button>
      </div>
    </div>
  );
};

export default CampaignGenerationStep;